import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Piano, ArrowLeft, Check, Users, Mic2 } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const FOCUS = [
  { id: "solo", icon: Piano, label: "Solo playing", blurb: "Playing beautifully on your own" },
  { id: "band", icon: Users, label: "Playing in a band", blurb: "Locking in with other musicians" },
  { id: "accompanying", icon: Mic2, label: "Accompanying singers", blurb: "Supporting a vocalist sensitively" },
];

export default function PianoWaitlist() {
  const { user } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [focus, setFocus] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const label = FOCUS.find((f) => f.id === focus)?.label || "";
      await api.post("/waitlist", { name, email, focus: label, message: message || null });
      setDone(true);
      toast.success("You're on the list — thank you!");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen text-foreground">
      <Navbar />
      <div className="mx-auto max-w-3xl px-4 sm:px-8 py-12 lg:py-16">
        <Link to="/" data-testid="waitlist-back" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary">
          <ArrowLeft size={15} /> Back to the hub
        </Link>

        <div className="mt-6 flex items-center gap-3">
          <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary"><Piano size={24} /></span>
          <span className="rounded-full bg-secondary px-3 py-1 text-[11px] uppercase tracking-widest text-muted-foreground">Coming soon</span>
        </div>
        <h1 className="mt-5 font-serif text-4xl tracking-tight sm:text-5xl">The piano course</h1>
        <p className="mt-4 max-w-xl text-muted-foreground">
          Three worlds, one instrument — <span className="text-foreground">solo playing</span>, <span className="text-foreground">playing in a band</span> and{" "}
          <span className="text-foreground">accompanying singers</span>. I'm building it now. Join the waitlist and you'll be the
          first to know when it opens (and get an early-bird price).
        </p>

        {done ? (
          <div className="mt-10 rounded-2xl border border-primary/40 bg-primary/10 p-8 text-center" data-testid="waitlist-success">
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-primary text-primary-foreground"><Check size={26} /></span>
            <h2 className="mt-5 font-serif text-2xl">You're on the list!</h2>
            <p className="mt-3 text-muted-foreground">Thanks {name.split(" ")[0]} — I'll email {email} the moment the piano course opens.</p>
            <Link to="/courses" className="mt-6 inline-block"><Button className="rounded-full bg-primary text-foreground hover:bg-primary/90">Explore courses available now</Button></Link>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-10 space-y-6" data-testid="waitlist-form">
            <div>
              <Label className="text-muted-foreground">Which interests you most?</Label>
              <div className="mt-3 grid gap-3 sm:grid-cols-3">
                {FOCUS.map((f) => (
                  <button
                    key={f.id} type="button" onClick={() => setFocus(f.id)}
                    data-testid={`waitlist-focus-${f.id}`}
                    className={`rounded-2xl border-2 p-4 text-left transition-all ${focus === f.id ? "border-primary bg-primary/10" : "border-border bg-card hover:border-primary"}`}
                  >
                    <f.icon size={20} className="text-primary" />
                    <p className="mt-2 text-sm font-medium">{f.label}</p>
                    <p className="mt-0.5 text-xs text-muted-foreground">{f.blurb}</p>
                  </button>
                ))}
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="wl-name" className="text-muted-foreground">Name</Label>
                <Input id="wl-name" required value={name} onChange={(e) => setName(e.target.value)} data-testid="waitlist-name" placeholder="Your name" className="mt-1 border-border bg-card" />
              </div>
              <div>
                <Label htmlFor="wl-email" className="text-muted-foreground">Email</Label>
                <Input id="wl-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="waitlist-email" placeholder="you@example.com" className="mt-1 border-border bg-card" />
              </div>
            </div>
            <div>
              <Label htmlFor="wl-msg" className="text-muted-foreground">Anything you'd love to learn? (optional)</Label>
              <Textarea id="wl-msg" value={message} onChange={(e) => setMessage(e.target.value)} data-testid="waitlist-message" placeholder="Tell me about your playing and goals…" className="mt-1 border-border bg-card" rows={4} />
            </div>
            <Button type="submit" disabled={loading} data-testid="waitlist-submit" className="w-full rounded-full bg-primary py-6 text-base text-foreground hover:bg-primary/90">
              {loading ? "Joining…" : "Join the waitlist"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
